// useApiHealthMonitor — global watcher for backend gateway-style failures.
//
// During a deploy (`docker compose up -d --force-recreate hydra`) the
// hydra container is dead for ~20-40s. nginx returns 502 / 503 / 504
// to every in-flight request. Without intervention, every component
// using TanStack Query renders its own error state — the dashboard,
// the live progress poller, the engagements list — all at once. Ugly.
//
// This hook subscribes to the QueryCache and counts gateway failures
// in a sliding window. When the threshold is hit, it navigates the
// user to the branded /503 page (preserving the current URL in route
// state so they can be sent back when the API recovers). The /503
// page polls /health and auto-navigates back the moment it's reachable.
//
// Mounted once at the root via <RootLayout/>. The hook is component-
// based (not module-level) so it lives inside the router context and
// can call useNavigate / useLocation.

import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/lib/api";

const GATEWAY_STATUSES = new Set([502, 503, 504]);

// Trip the global handler when at least N gateway failures land within
// the window. Below either threshold, treat as a transient blip and
// let the component-level error states do their thing.
const FAILURE_WINDOW_MS = 10_000;
const FAILURE_THRESHOLD = 2;

export function useApiHealthMonitor() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const failuresRef = useRef<number[]>([]);

  useEffect(() => {
    const cache = queryClient.getQueryCache();
    const unsub = cache.subscribe((event) => {
      if (event.type !== "updated") return;
      if (event.action.type !== "error") return;

      const err = event.action.error;
      if (!(err instanceof ApiError)) return;
      if (!GATEWAY_STATUSES.has(err.status)) return;

      const now = Date.now();
      // Drop failures that fell out of the sliding window.
      failuresRef.current = failuresRef.current.filter(
        (t) => now - t < FAILURE_WINDOW_MS,
      );
      failuresRef.current.push(now);

      if (failuresRef.current.length >= FAILURE_THRESHOLD) {
        // Don't loop: if we're already on /503 the user is being
        // recovered by the page's own /health poll.
        if (location.pathname === "/503") return;
        // Reset the counter so we don't immediately re-navigate after
        // the user has been bounced (the cache will keep firing errors
        // for a moment as in-flight queries resolve).
        failuresRef.current = [];
        navigate("/503", {
          state: { returnTo: location.pathname + location.search },
        });
      }
    });
    return unsub;
  }, [queryClient, navigate, location.pathname, location.search]);
}
